import React, { useState } from 'react'
import { 
  Link, Globe, ChefHat, Clock, Users, AlertCircle, 
  CheckCircle, Loader, X, Eye, Import, BookOpen
} from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'

interface RecipePreview {
  name: string
  description?: string
  ingredient_count: number
  instruction_count: number
  prep_time: number
  cook_time: number
  servings: number
  has_image: boolean
  has_nutrition: boolean
  source_url: string
}

interface RecipeImporterProps {
  onImportSuccess?: (recipeId: string) => void
  onClose?: () => void
}

export const RecipeImporter: React.FC<RecipeImporterProps> = ({ 
  onImportSuccess, 
  onClose 
}) => {
  const [url, setUrl] = useState('')
  const [loading, setLoading] = useState(false)
  const [preview, setPreview] = useState<RecipePreview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [importing, setImporting] = useState(false)

  // Popular recipe sites for quick reference
  const popularSites = [
    { name: 'AllRecipes', domain: 'allrecipes.com', icon: '🍳' },
    { name: 'Food Network', domain: 'foodnetwork.com', icon: '📺' },
    { name: 'Serious Eats', domain: 'seriouseats.com', icon: '🔬' },
    { name: 'NYT Cooking', domain: 'cooking.nytimes.com', icon: '📰' },
    { name: 'Bon Appétit', domain: 'bonappetit.com', icon: '🥘' },
    { name: 'Simply Recipes', domain: 'simplyrecipes.com', icon: '🥗' },
    { name: 'BBC Good Food', domain: 'bbcgoodfood.com', icon: '🇬🇧' },
    { name: 'Tasty', domain: 'tasty.co', icon: '🎬' }
  ]

  const handlePreview = async () => {
    if (!url.trim()) {
      setError('Please enter a recipe URL')
      return
    }

    // Basic URL validation
    try {
      new URL(url)
    } catch {
      setError('Please enter a valid URL')
      return
    }

    setLoading(true)
    setError(null)
    setPreview(null)

    try {
      const response = await apiClient.get('/api/recipes/import-preview', {
        params: { url }
      })

      if (response.error) {
        setError(response.error)
      } else if (response.preview) {
        setPreview(response.preview)
        if (response.warning) {
          toast(response.warning)
        }
      }
    } catch (err: any) {
      // Handle error response properly - extract string message
      const errorMessage = err.response?.data?.detail || 
                          err.response?.data?.error || 
                          err.message || 
                          'Failed to preview recipe'
      setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage))
    } finally {
      setLoading(false)
    }
  }

  const handleImport = async () => {
    if (!preview) return

    setImporting(true)
    setError(null)

    try {
      const response = await apiClient.post('/api/recipes/import-url', { url })
      
      if (response.success) {
        toast.success(`Successfully imported: ${response.recipe.name}`, {
          duration: 5000,
          icon: '🎉'
        })
        
        if (onImportSuccess) {
          onImportSuccess(response.recipe_id)
        }
        
        // Reset form
        setUrl('')
        setPreview(null)
      }
    } catch (err: any) {
      // Handle error response properly - extract string message
      const errorMessage = err.response?.data?.detail || 
                          err.response?.data?.error || 
                          err.message || 
                          'Failed to import recipe'
      setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage))
      toast.error('Import failed - check the error message')
    } finally {
      setImporting(false)
    }
  }

  const isSupported = (url: string) => {
    return popularSites.some(site => url.includes(site.domain))
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold flex items-center">
            <Globe className="w-6 h-6 mr-2 text-blue-500" />
            Import Recipe from Website
          </h2>
          <p className="text-gray-600 mt-1">
            Paste a recipe URL to automatically import ingredients and instructions
          </p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* URL Input */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Recipe URL
          </label>
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <input
                type="url"
                value={url}
                onChange={(e) => {
                  setUrl(e.target.value)
                  setError(null)
                }}
                placeholder="https://www.example.com/recipe..."
                className="w-full px-4 py-2 pl-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={loading || importing}
              />
              <Link className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              {url && isSupported(url) && (
                <CheckCircle className="absolute right-3 top-2.5 w-4 h-4 text-green-500" />
              )}
            </div>
            <button
              onClick={handlePreview}
              disabled={loading || importing || !url.trim()}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4" />
                  Preview
                </>
              )}
            </button>
          </div>
        </div>

        {/* Supported Sites */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            Supported Recipe Sites
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {popularSites.map(site => (
              <div
                key={site.domain}
                className="flex items-center gap-1 text-xs text-gray-600"
              >
                <span>{site.icon}</span>
                <span>{site.name}</span>
                {url.includes(site.domain) && (
                  <CheckCircle className="w-3 h-3 text-green-500 ml-auto" />
                )}
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            * Most recipe websites with structured data are supported
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-red-700">{error}</p>
              <p className="text-xs text-red-600 mt-1">
                Try copying the URL directly from your browser's address bar
              </p>
            </div>
          </div>
        )}

        {/* Preview */}
        {preview && (
          <div className="border rounded-lg p-4 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold flex items-center">
                  <ChefHat className="w-5 h-5 mr-2 text-orange-500" />
                  {preview.name}
                </h3>
                {preview.description && (
                  <p className="text-sm text-gray-600 mt-1">{preview.description}</p>
                )}
              </div>
              <div className="flex gap-2">
                {preview.has_image && (
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                    Has Image
                  </span>
                )}
                {preview.has_nutrition && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                    Nutrition Info
                  </span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <Clock className="w-5 h-5 mx-auto text-gray-600 mb-1" />
                <p className="text-xs text-gray-500">Prep Time</p>
                <p className="font-semibold">{preview.prep_time || 0} min</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <Clock className="w-5 h-5 mx-auto text-gray-600 mb-1" />
                <p className="text-xs text-gray-500">Cook Time</p>
                <p className="font-semibold">{preview.cook_time || 0} min</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <Users className="w-5 h-5 mx-auto text-gray-600 mb-1" />
                <p className="text-xs text-gray-500">Servings</p>
                <p className="font-semibold">{preview.servings || 0}</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <BookOpen className="w-5 h-5 mx-auto text-gray-600 mb-1" />
                <p className="text-xs text-gray-500">Steps</p>
                <p className="font-semibold">{preview.instruction_count || 0}</p>
              </div>
            </div>

            {/* Ingredients Preview */}
            {preview.ingredients && preview.ingredients.length > 0 && (
              <div className="border-t pt-4">
                <h4 className="font-semibold text-gray-700 mb-2">Ingredients:</h4>
                <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                  {preview.ingredients.slice(0, 5).map((ing: any, idx: number) => (
                    <li key={idx}>
                      {ing.quantity && `${ing.quantity} `}
                      {ing.unit && `${ing.unit} `}
                      {ing.name}
                    </li>
                  ))}
                  {preview.ingredient_count > 5 && (
                    <li className="text-gray-500 italic">
                      ... and {preview.ingredient_count - 5} more
                    </li>
                  )}
                </ul>
              </div>
            )}

            {/* Instructions Preview */}
            {preview.instructions && preview.instructions.length > 0 && (
              <div className="border-t pt-4">
                <h4 className="font-semibold text-gray-700 mb-2">Instructions:</h4>
                <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1">
                  {preview.instructions.slice(0, 3).map((inst: any, idx: number) => (
                    <li key={idx}>
                      {inst.instruction}
                    </li>
                  ))}
                  {preview.instruction_count > 3 && (
                    <li className="text-gray-500 italic">
                      ... and {preview.instruction_count - 3} more steps
                    </li>
                  )}
                </ol>
              </div>
            )}

            <div className="flex items-center justify-between pt-4 border-t">
              <div className="text-sm text-gray-600">
                <span className="font-medium">{preview.ingredient_count || 0}</span> ingredients
                {' · '}
                <span className="font-medium">{preview.instruction_count || 0}</span> steps
              </div>
              <button
                onClick={handleImport}
                disabled={importing}
                className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {importing ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Import className="w-4 h-4" />
                    Import Recipe
                  </>
                )}
              </button>
            </div>

            <div className="text-xs text-gray-500">
              Source: <a href={preview.source_url} target="_blank" rel="noopener noreferrer" 
                className="text-blue-500 hover:underline">
                {new URL(preview.source_url).hostname}
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}